import { CoaTemplateApplySource } from "@common/types/domains/accounting/chartOfAccountsTemplateApplyEvent";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import type {
    ChartOfAccountsTemplateApplyEventDetail,
    CoaApplyUndoEligibility,
} from "@common/schemas/domains/accounting/chart-of-accounts-template-apply-event.schema";
import { canRoleEditCoa } from "@hooks/permissions/canRoleEditCoa";
import {
    isChangeSetEmpty,
    type CoaTemplateActivityEventResult,
} from "./coaTemplateActivityEvents";

// Safety-based, per-entity undo classification derived purely from the loaded
// event detail + eligibility. It intentionally ignores the current user's
// permission and the feature flag: those change whether the user *may* undo,
// not whether the underlying entity *can* be undone. "fullyBlocked" is what the
// subtitle counts and the rail greys; "partial" entities stay selectable.
export type EntityUndoAvailability =
    | { status: "eligible" }
    | { status: "partial" }
    | { status: "fullyBlocked" }
    | { status: "reverted" }
    | { status: "notApplicable" };

export function getEntityUndoAvailability({
    detail,
    eligibility,
}: {
    detail: ChartOfAccountsTemplateApplyEventDetail;
    eligibility: CoaApplyUndoEligibility | undefined;
}): EntityUndoAvailability {
    if (detail.revertedAt !== null) {
        return { status: "reverted" };
    }
    if (detail.source !== CoaTemplateApplySource.WIZARD) {
        return { status: "notApplicable" };
    }
    if (isChangeSetEmpty(detail)) {
        return { status: "notApplicable" };
    }
    if (!eligibility) {
        // Loading/error: fail safe rather than falsely claiming it's blocked.
        return { status: "notApplicable" };
    }
    // A superseded event returns empty eligibility items, but its newer apply
    // means nothing here can be undone.
    if (eligibility.superseded || eligibility.eligibleCount === 0) {
        return { status: "fullyBlocked" };
    }
    if (eligibility.skippedCount > 0) {
        return { status: "partial" };
    }
    return { status: "eligible" };
}

export function getFullyBlockedEntityCount(
    events: CoaTemplateActivityEventResult[],
): number {
    let count = 0;
    for (const event of events) {
        if (event.status !== "loaded") {
            continue;
        }
        if (
            getEntityUndoAvailability({
                detail: event.detail,
                eligibility: event.eligibility,
            }).status === "fullyBlocked"
        ) {
            count += 1;
        }
    }
    return count;
}

function roleCanEditCoa(role: UserWorkspaceRole | undefined): boolean {
    return role !== undefined && canRoleEditCoa(role);
}

// The subtitle warning belongs to the undo affordance, so it is suppressed
// entirely in read-only contexts (flag off / non-apply source) and when the
// user cannot edit any of the loaded workspaces.
export function getUndoSubtitleSuffix({
    events,
    isReadOnly,
    workspaceRolesById,
}: {
    events: CoaTemplateActivityEventResult[];
    isReadOnly: boolean;
    workspaceRolesById: ReadonlyMap<string, UserWorkspaceRole>;
}): string | null {
    if (isReadOnly) {
        return null;
    }
    const hasEditPermission = events.some(
        (event) =>
            event.status === "loaded" &&
            roleCanEditCoa(workspaceRolesById.get(event.eventRef.workspaceId)),
    );
    if (!hasEditPermission) {
        return null;
    }
    const count = getFullyBlockedEntityCount(events);
    return count > 0 ? `\u00b7 ${count} can\u2019t be undone` : null;
}
